// Rapportvakt — the modal shell every sheet in the product sits in.
//
// Started life inside the app page, next to the upgrade and remove sheets. The
// publisher index needed the same thing, so it lives here now: one set of close
// rules (Escape, a click on the backdrop) rather than two that drift apart.

import React, { useEffect, useRef } from 'react';
import { Glyph } from './Delivery';

/**
 * Modal shell: Escape and a click on the backdrop both close it.
 *
 * Focus lands on the panel itself by default. Pass `initialFocusRef` (a ref
 * already attached to a field inside `children`) to focus that field
 * instead — refs commit before this effect runs, so it is populated in
 * time. Children mount (and their own effects run) before this one, so a
 * field-focusing effect in a child would otherwise lose the race to this
 * one; routing it through this same effect avoids that.
 */
export default function Sheet({ labelledBy, onClose, className = '', initialFocusRef, children }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const target = (initialFocusRef && initialFocusRef.current) || panelRef.current;
    if (target) target.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, initialFocusRef]);

  return (
    <div
      className="rv-sheet"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`rv-sheet__panel${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        ref={panelRef}
      >
        {children}
      </div>
    </div>
  );
}

/** The round close button in a sheet's top row. */
export function SheetClose({ label, onClose }) {
  return (
    <button type="button" className="rv-sheet__close" onClick={onClose} aria-label={label}>
      <Glyph name="cross" size={14} />
    </button>
  );
}
